import { useCallback, useEffect, useState } from "react";
import { Check, ListChecks, LoaderCircle, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const CATEGORIES = [
  { value: "connaissance", label: "Connaissance" },
  { value: "mise_en_situation", label: "Mise en situation" },
];

function QuestionModal({ initial, themes, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? {
    theme: initial.theme, category: initial.category, question: initial.question,
    options: [...initial.options], correct: initial.correct, explanation: initial.explanation,
  } : { theme: themes[0], category: "connaissance", question: "", options: ["", "", "", ""], correct: 0, explanation: "" });
  const [saving, setSaving] = useState(false);

  const setOption = (i, v) => setForm((f) => {
    const options = [...f.options];
    options[i] = v;
    return { ...f, options };
  });

  const submit = async (e) => {
    e.preventDefault();
    const options = form.options.map((o) => o.trim()).filter(Boolean);
    if (options.length < 2) { toast.error("Au moins 2 options sont requises."); return; }
    if (form.correct >= options.length) { toast.error("La bonne réponse doit correspondre à une option remplie."); return; }
    setSaving(true);
    try {
      const payload = { ...form, options };
      if (initial) await api.patch(`/admin/questions/${initial.id}`, payload);
      else await api.post("/admin/questions", payload);
      toast.success(initial ? "Question mise à jour." : "Question créée.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer la question.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={onClose} data-testid="admin-question-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-lg font-extrabold text-slate-900">{initial ? "Modifier la question" : "Nouvelle question"}</h3>
          <button type="button" onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="admin-question-modal-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <select value={form.theme} onChange={(e) => setForm((f) => ({ ...f, theme: e.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-xs focus:border-bleu focus:outline-none" data-testid="admin-question-theme-select">
            {themes.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-xs focus:border-bleu focus:outline-none" data-testid="admin-question-category-select">
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <textarea value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))} required minLength={10} maxLength={500} rows={2}
          placeholder="Énoncé de la question…"
          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none" data-testid="admin-question-text-input" />
        <p className="mt-3 text-[11px] font-bold uppercase tracking-wider text-slate-400">Options (cochez la bonne réponse)</p>
        <div className="mt-1.5 space-y-2">
          {form.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="radio" name="correct" checked={form.correct === i} onChange={() => setForm((f) => ({ ...f, correct: i }))}
                className="h-4 w-4 text-bleu" data-testid={`admin-question-correct-${i}`} />
              <input value={o} onChange={(e) => setOption(i, e.target.value)} maxLength={300}
                placeholder={`Option ${i + 1}${i < 2 ? " (obligatoire)" : " (facultative)"}`}
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-bleu focus:outline-none" data-testid={`admin-question-option-${i}`} />
            </div>
          ))}
        </div>
        <textarea value={form.explanation} onChange={(e) => setForm((f) => ({ ...f, explanation: e.target.value }))} required minLength={5} maxLength={800} rows={2}
          placeholder="Explication affichée dans la correction…"
          className="mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none" data-testid="admin-question-explanation-input" />
        <button type="submit" disabled={saving} className="btn-primary mt-4 w-full !py-3 disabled:opacity-50" data-testid="admin-question-save">
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : initial ? "Enregistrer les modifications" : "Créer la question"}
        </button>
      </form>
    </div>
  );
}

export default function AdminQuestions() {
  const [data, setData] = useState(null);
  const [theme, setTheme] = useState("");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/questions", { params: { theme: theme || undefined, q: q || undefined } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les questions."));
  }, [theme, q]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    const question = toDelete;
    setToDelete(null);
    try {
      await api.delete(`/admin/questions/${question.id}`);
      toast.success("Question supprimée.");
      load();
    } catch { toast.error("Impossible de supprimer la question."); }
  };

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="page-admin-questions">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Questions civiques</h1>
          <p className="mt-1 text-sm text-slate-500">{data.total} questions dans la banque (quiz d'entraînement et examens blancs officiels 2026).</p>
        </div>
        <button onClick={() => setModal({ mode: "create" })} className="btn-primary !px-5 !py-2.5 !text-xs" data-testid="admin-question-create-button">
          <Plus className="h-3.5 w-3.5" /> Nouvelle question
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-52 flex-1">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une question…"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none"
            data-testid="admin-questions-search-input" />
        </div>
        <button onClick={() => setTheme("")}
          className={`rounded-full px-3.5 py-2 text-[11px] font-bold ${!theme ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500"}`}
          data-testid="admin-questions-filter-all">
          Toutes ({data.total})
        </button>
        {data.themes.map((t) => (
          <button key={t} onClick={() => setTheme(t)}
            className={`rounded-full px-3.5 py-2 text-[11px] font-bold ${theme === t ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"}`}
            data-testid="admin-questions-filter-theme">
            {t.split(" ")[0]}… ({data.theme_counts[t]})
          </button>
        ))}
      </div>

      {data.questions.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="admin-questions-empty">
          <ListChecks className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucune question trouvée</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.questions.map((question) => (
            <div key={question.id} className="card-integra p-4" data-testid="admin-question-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-bleu-light px-2.5 py-1 text-[10px] font-bold text-bleu">{question.theme}</span>
                {question.category === "mise_en_situation" && <span className="rounded-full bg-violet-100 px-2.5 py-1 text-[10px] font-bold text-violet-700">Mise en situation</span>}
                {question.custom && <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700">Ajoutée par l'équipe</span>}
                <div className="ml-auto flex items-center gap-1.5">
                  <button onClick={() => setModal({ mode: "edit", question })} title="Modifier"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-bleu/40 hover:text-bleu" data-testid="admin-question-edit-button">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setToDelete(question)} title="Supprimer"
                    className="rounded-full border border-slate-200 p-2 text-slate-500 hover:border-rouge/40 hover:text-rouge" data-testid="admin-question-delete-button">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-sm font-bold text-slate-900">{question.question}</p>
              <ul className="mt-2 space-y-1">
                {question.options.map((o, i) => (
                  <li key={i} className={`flex items-start gap-1.5 text-xs ${i === question.correct ? "font-bold text-emeraude" : "text-slate-500"}`}>
                    {i === question.correct ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <span className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <QuestionModal initial={modal.mode === "edit" ? modal.question : null} themes={data.themes}
          onClose={() => setModal(null)} onSaved={load} />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="admin-question-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette question ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toDelete?.question} » sera définitivement retirée de la banque de questions des quiz et examens blancs. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-question-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rouge text-white hover:bg-red-700" data-testid="admin-question-delete-confirm">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
